import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { SubsectionTitle, BodyText, MetaText } from '../ui/typography';
import type { HydratedBlogPost } from '../../types/blog';
import { CATEGORY_LABELS } from '../../constants/blog';
import { calculateReadTime } from '../../../lib/readTime';
import { toTitleCase } from '../../../lib/titleCase';
import { formatDate } from '../../../lib/formatDate';
import PostCoverImage from './PostCoverImage';

interface PostCardProps {
    post: HydratedBlogPost;
    /**
     * Precomputed read-time, in minutes (audit H7).
     *
     * The prerendered listing pages carry post summaries WITHOUT `body` — shipping
     * eleven article bodies to render "5 min read" would several-fold the weight of
     * /blog — so the build computes this with the same util and passes the number.
     * Omitted everywhere else, where the full post is in hand and this derives from
     * the body exactly as before.
     */
    readTime?: number | undefined;
}

const PostCard: React.FC<PostCardProps> = ({ post, readTime: readTimeProp }) => {
    // Same source of truth as BlogPostPage's metadata strip — derived from the
    // post body via the shared util, so card and article always agree. A supplied
    // readTime wins because it came from that same util at build time, over a body
    // the listing payload deliberately omits.
    const readTime = useMemo(
        () => readTimeProp ?? calculateReadTime(post.body),
        [readTimeProp, post.body],
    );

    return (
        // Matches GymCard hover treatment — root-level border shift to brand-primary/50 with a
        // 300ms transition. Overrides Card's default hover (hover:border-border-strong) and
        // transition (transition-colors duration-150) via tailwind-merge in the className prop.
        <Card variant="interactive" className="group transition-all duration-300 hover:border-brand-primary/50">
            <Link
                to={`/blog/${post.category}/${post.slug}`}
                className="block rounded-2xl focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-primary focus-visible:ring-offset-0 focus-visible:border-brand-primary"
            >
                {/* 2:1 cover to match GymCard's image ratio (A2). Scoped here, not on
                    PostCoverImage's default, so BlogPostPage's article hero keeps 16:9. */}
                <PostCoverImage post={post} loading="lazy" className="aspect-[2/1]" />
                <div className="p-4 space-y-3">
                    {/* Title truncated to 1 line (fills to the character, then ellipsis —
                        `truncate`, not `line-clamp-1` which breaks at the whole-word boundary and
                        drops trailing words) + excerpt reserved at 2 lines (min-h-[2lh])
                        → every card computes to a uniform height. */}
                    <SubsectionTitle className="truncate">{toTitleCase(post.title)}</SubsectionTitle>
                    <BodyText className="line-clamp-2 min-h-[2lh] text-text-muted">{post.excerpt}</BodyText>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        {/* Bottom-left: date · read-time — matches BlogPostPage's metadata strip */}
                        <div className="flex items-center gap-2 whitespace-nowrap">
                            <MetaText>{formatDate(post.published_at)}</MetaText>
                            <span aria-hidden="true" className="text-sm text-text-muted">·</span>
                            <MetaText>{readTime} min read</MetaText>
                        </div>
                        {/* Bottom-right: category */}
                        <Badge variant="category">{CATEGORY_LABELS[post.category]}</Badge>
                    </div>
                </div>
            </Link>
        </Card>
    );
};

export default PostCard;
